import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Shield, Download, FileText, AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, Lock, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ComplianceDashboard = ({ token }) => {
  const [documents, setDocuments] = useState([]);
  const [reports, setReports] = useState({ daily: [], weekly: [] });
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [activeView, setActiveView] = useState("documents");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [docsRes, dailyRes, weeklyRes, logRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/admin/compliance/documents`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/security-reports?report_type=daily&limit=7`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/security-reports?report_type=weekly&limit=4`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/audit-log?limit=30`, authHeaders),
      ]);
      setDocuments(docsRes.data.documents || []);
      setReports({ daily: dailyRes.data.reports || [], weekly: weeklyRes.data.reports || [] });
      setAuditLog(logRes.data.logs || []);
    } catch (err) {
      console.error("ComplianceDashboard fetch error:", err);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async (docId, docName) => {
    setDownloading(docId);
    try {
      const response = await axios.get(
        `${API}/api/mybookie/admin/compliance/download/${docId}`,
        { ...authHeaders, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MyBookie_${docId.replace(/-/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    } finally { setDownloading(null); }
  };

  const severityColor = (s) => ({
    info: 'text-blue-600 bg-blue-50', warning: 'text-amber-600 bg-amber-50',
    high: 'text-red-600 bg-red-50', critical: 'text-red-700 bg-red-100',
  }[s] || 'text-gray-600 bg-gray-50');

  const actionColor = (a) => ({
    LOGIN_SUCCESS: 'text-green-600', LOGIN_FAILED: 'text-red-500', LOGOUT: 'text-[#5C6661]',
    REGISTER: 'text-blue-600', PASSWORD_CHANGE: 'text-amber-600', ACCOUNT_LOCKED: 'text-red-600',
    ACCOUNT_UNLOCKED: 'text-green-600', SESSION_REVOKED: 'text-amber-500', SECURITY_ALERT: 'text-red-700',
  }[a] || 'text-[#5C6661]');

  const docIcon = (id) => ({
    'security-policy': Shield, 'incident-response': AlertTriangle, 'access-control': Lock,
    'security-audit-report': Eye,
  }[id] || FileText);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-5 h-5 animate-spin text-[#0A2B1D] mr-2" />
        <span className="text-[#5C6661]">Loading compliance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#111614] flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0A2B1D]" /> Security & Compliance
          </h2>
          <p className="text-sm text-[#5C6661]">Download policy documents, view audit reports, and monitor security events</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="rounded-sm" data-testid="refresh-compliance">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-[#F6F5F2] p-1 rounded-sm">
        {[
          { id: 'documents', label: 'Policy Documents', icon: FileText },
          { id: 'reports', label: 'Security Reports', icon: Shield },
          { id: 'audit-log', label: 'Audit Log', icon: Eye },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveView(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-sm rounded-sm transition-colors ${
              activeView === id ? 'bg-white text-[#0A2B1D] font-medium shadow-sm' : 'text-[#5C6661] hover:text-[#111614]'
            }`} data-testid={`compliance-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* ===== DOCUMENTS VIEW ===== */}
      {activeView === 'documents' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => {
            const Icon = docIcon(doc.id);
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-[#E2ECE7] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4.5 h-4.5 text-[#0A2B1D]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm text-[#111614]">{doc.name}</h3>
                        <p className="text-xs text-[#8A948F] mt-1">{doc.desc}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs flex-shrink-0 ml-3"
                      onClick={() => handleDownload(doc.id, doc.name)}
                      disabled={downloading === doc.id}
                      data-testid={`download-${doc.id}`}>
                      {downloading === doc.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ===== SECURITY REPORTS VIEW ===== */}
      {activeView === 'reports' && (
        <>
          {/* Daily Reports */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Daily Security Audits (last 7 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {reports.daily.length === 0 ? (
                <p className="text-sm text-[#5C6661] text-center py-4">No daily reports yet. First report generates 60s after server startup.</p>
              ) : (
                <div className="space-y-3">
                  {reports.daily.map((r, idx) => (
                    <div key={r.timestamp} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-[#5C6661]" />
                          <span className="text-sm font-medium">{r.timestamp?.slice(0, 10)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${severityColor(r.severity)}`}>{r.severity}</span>
                        </div>
                        <span className="text-xs text-[#8A948F]">{r.alerts?.length || 0} alert(s)</span>
                      </div>
                      {/* Key metrics */}
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs mb-2">
                        {[
                          { l: 'Failed Logins', v: r.checks?.failed_logins_24h },
                          { l: 'Users', v: r.checks?.total_users },
                          { l: 'No 2FA', v: r.checks?.users_without_2fa },
                          { l: 'Active Sessions', v: r.checks?.active_sessions },
                          { l: 'Locked', v: r.checks?.locked_accounts },
                          { l: 'JWT Strong', v: r.checks?.jwt_secret_strong ? 'Yes' : 'No' },
                        ].map(m => (
                          <div key={m.l} className="bg-[#F6F5F2] rounded p-1.5 text-center">
                            <p className="text-[10px] text-[#8A948F]">{m.l}</p>
                            <p className="font-medium text-[#111614]">{m.v ?? '—'}</p>
                          </div>
                        ))}
                      </div>
                      {/* Alerts */}
                      {r.alerts?.length > 0 && (
                        <div className="space-y-1">
                          {r.alerts.map((a, i) => (
                            <p key={`${r.timestamp}-alert-${i}-${a.slice(0,20)}`} className="text-xs text-amber-700 flex items-start gap-1.5">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {a}
                            </p>
                          ))}
                        </div>
                      )}
                      {(!r.alerts || r.alerts.length === 0) && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> All security checks passed
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly Reports */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Weekly Full Audits (last 4 weeks)</CardTitle>
            </CardHeader>
            <CardContent>
              {reports.weekly.length === 0 ? (
                <p className="text-sm text-[#5C6661] text-center py-4">No weekly reports yet. First report generates 5 minutes after server startup.</p>
              ) : (
                <div className="space-y-3">
                  {reports.weekly.map((r) => (
                    <div key={r.timestamp} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-[#5C6661]" />
                          <span className="text-sm font-medium">{r.timestamp?.slice(0, 10)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${severityColor(r.severity)}`}>{r.severity}</span>
                        </div>
                      </div>
                      {r.metrics && (
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-xs mb-2">
                          {[
                            { l: '2FA Adoption', v: `${r.metrics["2fa_adoption_pct"] || 0}%` },
                            { l: 'Login Fail Rate', v: `${r.metrics.login_failure_rate_pct || 0}%` },
                            { l: 'Failed Logins', v: r.metrics.failed_logins_week },
                            { l: 'Active Sessions', v: r.metrics.active_sessions },
                            { l: 'Inactive 30d', v: r.metrics.inactive_30d },
                          ].map(m => (
                            <div key={m.l} className="bg-[#F6F5F2] rounded p-1.5 text-center">
                              <p className="text-[10px] text-[#8A948F]">{m.l}</p>
                              <p className="font-medium text-[#111614]">{m.v ?? '—'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Security controls status */}
                      {r.security_controls_status && (
                        <details className="mt-2">
                          <summary className="text-xs text-[#0A2B1D] cursor-pointer font-medium">Security Controls Status</summary>
                          <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                            {Object.entries(r.security_controls_status).map(([k, v]) => (
                              <div key={k} className="flex justify-between bg-[#F6F5F2] rounded px-2 py-1">
                                <span className="text-[#5C6661] capitalize">{k.replace(/_/g, ' ')}</span>
                                <span className={v.includes("ACTIVE") || v === "YES" || v === "STRONG" || v === "AVAILABLE" ? 'text-green-600 font-medium' : 'text-amber-600'}>{v}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      {r.alerts?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {r.alerts.map((a, i) => (
                            <p key={`${r.timestamp}-walert-${i}-${a.slice(0,20)}`} className="text-xs text-amber-700 flex items-start gap-1.5">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {a}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== AUDIT LOG VIEW ===== */}
      {activeView === 'audit-log' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recent Security Events (last 30)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {auditLog.length === 0 ? (
              <p className="text-sm text-[#5C6661] text-center py-8">No audit log entries yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[#F6F5F2]">
                    <th className="text-left py-2 px-3 text-xs font-medium">Timestamp</th>
                    <th className="text-left py-2 px-3 text-xs font-medium">Action</th>
                    <th className="text-left py-2 px-3 text-xs font-medium">User</th>
                    <th className="text-left py-2 px-3 text-xs font-medium">IP</th>
                    <th className="text-left py-2 px-3 text-xs font-medium">Details</th>
                    <th className="text-center py-2 px-3 text-xs font-medium">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((log, idx) => (
                    <tr key={`${log.timestamp}-${idx}`} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 text-xs text-[#5C6661] whitespace-nowrap">{log.timestamp?.slice(0, 19)?.replace('T', ' ')}</td>
                      <td className={`py-2 px-3 text-xs font-medium ${actionColor(log.action)}`}>{log.action}</td>
                      <td className="py-2 px-3 text-xs text-[#5C6661]">{log.user_email || '—'}</td>
                      <td className="py-2 px-3 text-xs text-[#8A948F] font-mono">{log.ip_address || '—'}</td>
                      <td className="py-2 px-3 text-xs text-[#5C6661] max-w-[200px] truncate">{log.details || '—'}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${severityColor(log.severity)}`}>{log.severity}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { ComplianceDashboard };
export default ComplianceDashboard;
